/* global console window $ */
import * as React from "react";
import { useDispatch } from "react-redux";
import PropType from "prop-types";
import { startDrag } from "../../helpers/dragAndDrop";
import { getWeights } from "../../helpers/grid";
import borderHandleUp from "../../assets/icons/resize-handle-top.svg";
import borderHandleDown from "../../assets/icons/resize-handle-bottom.svg";
import borderHandleLeft from "../../assets/icons/resize-handle-left.svg";
import borderHandleRight from "../../assets/icons/resize-handle-right.svg";
import { remove, update } from "../../state/bordersSlice";

const BorderPinHandler = (props) => {
  const dispatch = useDispatch();

  const onDragEnd = (e, ds) => {
    const dsd = { ...ds, devent: { clientX: e.clientX, clientY: e.clientY } };
    console.log("drag end", dsd);
    if (ds?.content?.border) {
      const { vertical, pinsRolled } = ds.content;
      const pinsRolledWithOffset = pinsRolled.map((p) => p + 28);
      const movingPos = vertical ? e.clientY : e.clientX;

      const dists = pinsRolledWithOffset.map((p) => Math.abs(p - movingPos));
      const smallest = Math.min(...dists);
      const foundPinIndex = dists.indexOf(smallest);
      console.log({ pinsRolled, pinsRolledWithOffset, movingPos, dists, smallest, foundPinIndex });

      if (dsd.content.from) {
        if (foundPinIndex < (dsd.content.border.to ?? Number.MAX_VALUE) && foundPinIndex < pinsRolled.length - 1) {
          dispatch(update({ id: dsd.content.border.id, from: foundPinIndex }));
        } else {
          dispatch(remove({ id: dsd.content.border.id }));
        }
      } else {
        if (foundPinIndex > dsd.content.border.from) {
          dispatch(update({ id: dsd.content.border.id, to: foundPinIndex }));
        } else {
          dispatch(remove({ id: dsd.content.border.id }));
        }
      }
    }
  };

  const onDragMove = (e, obj) => {
    const { vertical, fixedPos, crossAxisPos, pinsRolled } = obj.content;
    const movingPos = vertical ? e.clientY : e.clientX;

    // Find nearest pin for snapping the ghost line
    const pinsRolledWithOffset = pinsRolled.map((p) => p + 28);
    const dists = pinsRolledWithOffset.map((p) => Math.abs(p - movingPos));
    const smallest = Math.min(...dists);
    const foundPinIndex = dists.indexOf(smallest);
    const snappedPos = pinsRolledWithOffset[foundPinIndex];

    const start = Math.min(fixedPos, snappedPos);
    let length = Math.abs(fixedPos - snappedPos);

    // Adjust length to not overlap with fixed pin (16px margin)
    let lineStart = start;
    if (length > 16 && obj.content.to) {
      length -= 16;
      if (snappedPos > fixedPos) {
        lineStart += 16;
      }
    } else if (length <= 16 && obj.content.to) {
      length = 0; // Hide line if it's too short
    }

    // Second ghost line follows the cursor
    const cursorLineStart = Math.min(fixedPos, movingPos);
    let cursorLineLength = Math.abs(fixedPos - movingPos);
    let cursorLineOffset = cursorLineStart;
    if (cursorLineLength > 16 && obj.content.to) {
      cursorLineLength -= 16;
      if (movingPos > fixedPos) {
        cursorLineOffset += 16;
      }
    } else if (cursorLineLength <= 16 && obj.content.to) {
      cursorLineLength = 0;
    }

    if (vertical) {
      obj.previewElement.css({
        transform: "translate(" + (crossAxisPos - 1) + "px, 0px)",
      });
      obj.previewElement.find(".ghost-line").css({
        height: length + "px",
        transform: "translate(0px, " + lineStart + "px)",
      });
      obj.previewElement.find(".cursor-line").css({
        height: cursorLineLength + "px",
        transform: "translate(0px, " + cursorLineOffset + "px)",
      });
      obj.previewElement.find(".ghost-pin").css({
        transform: "translate(-7px, " + (movingPos - 8) + "px)",
      });
    } else {
      obj.previewElement.css({
        transform: "translate(0px, " + (crossAxisPos - 1) + "px)",
      });
      obj.previewElement.find(".ghost-line").css({
        width: length + "px",
        transform: "translate(" + lineStart + "px, 0px)",
      });
      obj.previewElement.find(".cursor-line").css({
        width: cursorLineLength + "px",
        transform: "translate(" + cursorLineOffset + "px, 0px)",
      });
      obj.previewElement.find(".ghost-pin").css({
        transform: "translate(" + (movingPos - 8) + "px, -7px)",
      });
    }
  };

  const icon =
    props.direction === "left"
      ? borderHandleLeft
      : props.direction === "right"
        ? borderHandleRight
        : props.direction === "down"
          ? borderHandleDown
          : borderHandleUp;

  const style =
    props.top === false && props.to // last horizontal border's right handle
      ? {
          alignContent: "end",
          height: props.border?.from === props.border?.to - 1 ? "auto" : "100%",
          width: 16,
          top: props.border?.from === props.border?.to - 1 ? -8 : 13,
          left: "calc(100% - 18px)",
        }
      : props.top && props.to // horizontal border's right handle
        ? {
            top: props.border?.from === props.border?.to - 1 ? -37 : -11,
            left: "calc(100% - 18px)",
          }
        : props.top === false && props.from // last horizontal border's left handle
          ? {
              alignContent: "end",
              height: "100%",
              width: 16,
              left: -2,
              top: 13,
            }
          : props.top && props.from // horizontal border's left handle
            ? {
                top: -11,
                left: -2,
              }
            : props.left === false && props.to // last vertical border's bottom handle
              ? {
                  display: "flex",
                  justifyContent: "right",
                  right: -8,
                  top: props.i === props.border?.from ? "calc(100% - 45px)" : "calc(100% - 8px)",
                }
              : props.left && props.to // vertical border's bottom handle
                ? {
                    left: -13,
                    top: props.i === props.border?.from ? "calc(100% - 45px)" : "calc(100% - 8px)",
                  }
                : props.left === false && props.from // last vertical border's top handle
                  ? {
                      display: "flex",
                      justifyContent: "right",
                      right: -8,
                      top: -8,
                    }
                  : {
                      // vertical border's top handle
                      left: -13,
                      top: -2,
                    };

  return (
    <div
      style={{
        position: "relative",
        zIndex: 2010,
        pointerEvents: "all",
        ...style,
      }}
      onMouseDown={(e) => {
        console.log("mouse down", e);
        const vertical = props.left !== undefined;
        const weights = getWeights(props.top === undefined ? props.rows : props.cols);

        let spacerSum = 0;
        const items = vertical ? props.rows : props.cols;
        items
          .filter((c) => c[0] === "s")
          .forEach((c) => {
            spacerSum += parseInt(c.slice(1));
          });

        const availableSize = vertical ? window.innerHeight - 56 : window.innerWidth - 56;
        const sumWeights = weights.reduce((sum, a, i) => (items[i][0] !== "s" ? sum + a : sum), 0);
        const unit = (availableSize - spacerSum) / sumWeights;

        const pins = items.map((r, i) => (r[0] === "s" ? weights[i] : weights[i] * unit));
        const pinsRolled = [
          0,
          ...pins.map((_, i) => {
            let p = 0;
            for (let j = 0; j < i + 1; j++) {
              p += pins[j];
            }
            return p;
          }),
        ];

        const otherItems = !vertical ? props.rows : props.cols;
        const otherWeights = getWeights(otherItems);
        let otherSpacerSum = 0;
        otherItems
          .filter((c) => c[0] === "s")
          .forEach((c) => {
            otherSpacerSum += parseInt(c.slice(1));
          });
        const otherAvailableSize = !vertical ? window.innerHeight - 56 : window.innerWidth - 56;
        const otherSumWeights = otherWeights.reduce((sum, a, i) => (otherItems[i][0] !== "s" ? sum + a : sum), 0);
        const otherUnit = (otherAvailableSize - otherSpacerSum) / otherSumWeights;
        const otherPins = otherItems.map((r, i) => (r[0] === "s" ? otherWeights[i] : otherWeights[i] * otherUnit));
        const otherPinsRolled = [
          0,
          ...otherPins.map((_, i) => {
            let p = 0;
            for (let j = 0; j < i + 1; j++) {
              p += otherPins[j];
            }
            return p;
          }),
        ];

        const fixedIndex = props.from ? (props.border.to ?? items.length) : props.border.from;
        const fixedPos = pinsRolled[fixedIndex] + 28;

        startDrag(e, {
          content: {
            border: props.border,
            vertical,
            from: props.from,
            to: props.to,
            fixedPos,
            movingFrom: props.from,
            crossAxisPos: otherPinsRolled[props.border.index] + 28,
            pinsRolled,
          },
          weights,
          previewContent: (function () {
            const container = $("<div></div>");
            const line = $("<div></div>")
              .css({
                width: vertical ? 2 : 0,
                height: vertical ? 0 : 2,
                backgroundColor: "#0065FD",
                opacity: 1,
                position: "absolute",
                left: 0,
                top: 0,
                zIndex: 999,
                boxShadow: "0 0 4px #0065FD",
                transition: "height 0.2s ease, width 0.2s ease, transform 0.2s ease",
              })
              .addClass("ghost-line");
            const cursorLine = $("<div></div>")
              .css({
                width: vertical ? 2 : 0,
                height: vertical ? 0 : 2,
                borderLeft: vertical ? "2px dashed #0065FD" : "none",
                borderTop: vertical ? "none" : "2px dashed #0065FD",
                opacity: 0.5,
                position: "absolute",
                left: 0,
                top: 0,
                zIndex: 998,
              })
              .addClass("cursor-line");
            const pin = $("<img></img>").attr("src", icon).addClass("ghost-pin").css({
              position: "absolute",
              left: 0,
              top: 0,
              opacity: "100%",
            });
            container.append(line).append(cursorLine).append(pin);
            return container;
          })(),
          onDragEnd,
          onDragMove,
        });
        e.preventDefault();
      }}
    >
      <img src={icon} />
    </div>
  );
};

BorderPinHandler.propTypes = {
  i: PropType.number,
  j: PropType.number,
  border: PropType.object.isRequired,
  left: PropType.bool,
  top: PropType.bool,
  cols: PropType.array,
  rows: PropType.array,
  from: PropType.bool,
  to: PropType.bool,
  direction: PropType.string,
};

export default BorderPinHandler;
